import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, roomsAPI } from '../services/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [memberActionLoading, setMemberActionLoading] = useState(false);
  
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, settingsData] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getSettings()
      ]);
      setStats(statsData.data.meetings);
      setSettings(settingsData.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMeetings = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getMeetings();
      setMeetings(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRooms = async () => {
    setRoomsLoading(true);
    try {
      const response = await adminAPI.getRooms();
      setRooms(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setRoomsLoading(false);
    }
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    setError('');
    if (tab === 'users') {
      await loadUsers();
    } else if (tab === 'meetings') {
      await loadMeetings();
    } else if (tab === 'rooms') {
      await loadRooms();
    } else if (tab === 'settings' && !settings) {
      try {
        const response = await adminAPI.getSettings();
        setSettings(response.data);
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminAPI.updateUserRole(userId, newRole);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStatusToggle = async (userId) => {
    try {
      await adminAPI.toggleUserStatus(userId);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    try {
      await adminAPI.deleteUser(userId);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSettingsUpdate = async (updates) => {
    try {
      const response = await adminAPI.updateSettings(updates);
      setSettings(response.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette salle ?')) return;
    try {
      await roomsAPI.deleteRoom(roomId);
      setRooms(rooms.filter(r => r.id !== roomId));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleRoomStatus = async (roomId) => {
    try {
      const room = rooms.find(r => r.id === roomId);
      if (!room) return;
      await roomsAPI.toggleRoomStatus(roomId);
      setRooms(rooms.map(r => r.id === roomId ? { ...r, isActive: !r.isActive } : r));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedRoomId || !newMemberEmail.trim()) return;
    setMemberActionLoading(true);
    try {
      await roomsAPI.addParticipant(selectedRoomId, newMemberEmail, 'member');
      setNewMemberEmail('');
      setShowAddMember(false);
      loadRooms();
    } catch (err) {
      setError(err.message);
    } finally {
      setMemberActionLoading(false);
    }
  };

  const handleRemoveMember = async (roomId, userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir exclure ce membre ?')) return;
    try {
      await roomsAPI.removeParticipant(roomId, userId);
      loadRooms();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading && !stats) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Gérez votre candidature</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="admin-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => handleTabChange('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'rooms' ? 'active' : ''}`}
          onClick={() => handleTabChange('rooms')}
        >
          Salles ({rooms.length})
        </button>
        <Link to="/admin/users" className="tab">
          Utilisateurs
        </Link>
        <Link to="/admin/statistics" className="tab">
          Statistiques
        </Link>
        <Link to="/admin/roles" className="tab">
          Roles
        </Link>
        <Link to="/admin/settings" className="tab">
          Paramètres
        </Link>
      </div>

      <div className="admin-content">

        {activeTab === 'overview' && stats && (
          <div className="overview-grid">
            <div className="stat-card">
              <h3>Total Réunions</h3>
              <p className="stat-value">{stats.total}</p>
            </div>
            <div className="stat-card">
              <h3>Planifiées</h3>
              <p className="stat-value">{stats.scheduled}</p>
            </div>
            <div className="stat-card success">
              <h3>Terminées</h3>
              <p className="stat-value">{stats.completed}</p>
            </div>
            <div className="stat-card warning">
              <h3>En cours</h3>
              <p className="stat-value">{stats.inProgress}</p>
            </div>
            <div className="stat-card secondary">
              <h3>Annulées</h3>
              <p className="stat-value">{stats.cancelled}</p>
            </div>
            <div className="stat-card">
              <h3>Durée Moyenne</h3>
              <p className="stat-value">{stats.avgDuration} min</p>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-table">
            <h2>Gestion des Utilisateurs</h2>
            <table>
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>
                      <select 
                        value={u.role} 
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      >
                        <option value="user">Participant</option>
                        <option value="moderator">Présentateur</option>
                        <option value="admin">Administrateur</option>
                      </select>
                    </td>
                    <td>
                      <span className={`status-badge ${u.isActive ? 'active' : 'inactive'}`}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button 
                        className="btn-small"
                        onClick={() => handleStatusToggle(u.id)}
                      >
                        Basculer
                      </button>
                      <button 
                        className="btn-small btn-danger"
                        onClick={() => handleDeleteUser(u.id)}
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'meetings' && (
          <div className="meetings-table">
            <h2>Gestion des Réunions</h2>
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Host</th>
                  <th>Date</th>
                  <th>Duration</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {meetings.map(m => (
                  <tr key={m.id}>
                    <td>{m.title}</td>
                    <td>{m.hostName}</td>
                    <td>{new Date(m.scheduledStart).toLocaleString()}</td>
                    <td>{m.duration} min</td>
                    <td>
                      <span className={`status-badge ${m.status}`}>
                        {m.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'settings' && settings && (
          <div className="settings-form">
            <h2>Application Settings</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleSettingsUpdate({
                allowRegistration: formData.get('allowRegistration') === 'on',
                requireEmailVerification: formData.get('requireEmailVerification') === 'on',
                enableChat: formData.get('enableChat') === 'on',
                enableScreenShare: formData.get('enableScreenShare') === 'on',
                enableRecording: formData.get('enableRecording') === 'on',
                maintenanceMode: formData.get('maintenanceMode') === 'on',
                maxParticipantsPerRoom: formData.get('maxParticipantsPerRoom'),
                defaultRoomDuration: formData.get('defaultRoomDuration'),
              });
            }}>
              <div className="form-group checkbox">
                <input 
                  type="checkbox" 
                  id="allowRegistration" 
                  name="allowRegistration"
                  defaultChecked={settings.allowRegistration}
                />
                <label htmlFor="allowRegistration">Allow User Registration</label>
              </div>
              
              <div className="form-group checkbox">
                <input 
                  type="checkbox" 
                  id="requireEmailVerification" 
                  name="requireEmailVerification"
                  defaultChecked={settings.requireEmailVerification}
                />
                <label htmlFor="requireEmailVerification">Require Email Verification</label>
              </div>
              
              <div className="form-group checkbox">
                <input 
                  type="checkbox" 
                  id="enableChat" 
                  name="enableChat"
                  defaultChecked={settings.enableChat}
                />
                <label htmlFor="enableChat">Enable Chat</label>
              </div>
              
              <div className="form-group checkbox">
                <input 
                  type="checkbox" 
                  id="enableScreenShare" 
                  name="enableScreenShare"
                  defaultChecked={settings.enableScreenShare}
                />
                <label htmlFor="enableScreenShare">Enable Screen Share</label>
              </div>
              
              <div className="form-group checkbox">
                <input 
                  type="checkbox" 
                  id="enableRecording" 
                  name="enableRecording"
                  defaultChecked={settings.enableRecording}
                />
                <label htmlFor="enableRecording">Enable Recording</label>
              </div>
              
              <div className="form-group checkbox">
                <input 
                  type="checkbox" 
                  id="maintenanceMode" 
                  name="maintenanceMode"
                  defaultChecked={settings.maintenanceMode}
                />
                <label htmlFor="maintenanceMode">Maintenance Mode</label>
              </div>
              
              <div className="form-group">
                <label htmlFor="maxParticipantsPerRoom">Max Participants per Room</label>
                <input 
                  type="number" 
                  id="maxParticipantsPerRoom" 
                  name="maxParticipantsPerRoom"
                  defaultValue={settings.maxParticipantsPerRoom}
                  min="2"
                  max="100"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="defaultRoomDuration">Default Room Duration (minutes)</label>
                <input 
                  type="number" 
                  id="defaultRoomDuration" 
                  name="defaultRoomDuration"
                  defaultValue={settings.defaultRoomDuration}
                  min="15"
                  max="480"
                />
              </div>
              
              <button type="submit" className="btn-primary">Save Settings</button>
            </form>
          </div>
        )}

{activeTab === 'rooms' && (
          <div className="rooms-table">
            <h2>Gestion des Salles ({rooms.length})</h2>
            {roomsLoading ? (
              <div className="rooms-loading">Chargement des salles...</div>
            ) : rooms.length === 0 ? (
              <div className="rooms-empty">Aucune salle trouvée</div>
            ) : (
              <div className="rooms-grid">
                {rooms.map(room => (
                  <div className="room-card" key={room.id}>
                    <div className="room-card-header">
                      <h3>{room.nom_salle || room.name}</h3>
                      <span className={`room-status-badge ${room.isActive ? 'active' : 'inactive'}`}>
                        {room.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="room-info">
                      <div className="room-info-item">
                        <span className="room-info-label">Créateur</span>
                        <span className="room-info-value">{room.createur_name || 'N/A'}</span>
                      </div>
                      <div className="room-info-item">
                        <span className="room-info-label">Membres</span>
                        <span className="room-info-value">{room.participant_count || 0}</span>
                      </div>
                    </div>
                    <div className="room-actions">
                      <button 
                        className="btn-small"
                        onClick={() => handleToggleRoomStatus(room.id)}
                      >
                        {room.isActive ? 'Désactiver' : 'Activer'}
                      </button>
                      <button 
                        className="btn-small"
                        onClick={() => {
                          setSelectedRoomId(room.id);
                          setShowAddMember(true);
                        }}
                      >
                        + Membre
                      </button>
                      <button 
                        className="btn-small btn-danger"
                        onClick={() => handleDeleteRoom(room.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showAddMember && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Ajouter un membre à la salle</h3>
              <button 
                className="close-btn" 
                onClick={() => setShowAddMember(false)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleAddMember}>
              <div className="modal-body">
                <p>Entrez l'email du membre à ajouter à cette salle.</p>
                <div className="form-group">
                  <label htmlFor="newMemberEmail">Email du membre</label>
                  <input
                    type="email"
                    id="newMemberEmail"
                    value={newMemberEmail}
                    onChange={(e) => setNewMemberEmail(e.target.value)}
                    placeholder="email@example.com"
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowAddMember(false)}
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={memberActionLoading}>
                  {memberActionLoading ? 'Ajout...' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;